import { setupInterceptors } from '@/utils/httpClient'
import url from '@/utils/url'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { ApolloClients } from '@vue/apollo-composable'
import 'animate.css'
import { createPinia } from 'pinia'
import { createApp, h, provide } from 'vue'
import App from './App.vue'
import { defaultApolloClient } from './composables/apollo-client'
import cpanel from './modules/cpanel/index'
import lmail from './modules/lmail/index'
import module from './modules/module/index'
import pbxware from './modules/pbxware/index'
import smsGlobal from './modules/sms-global/index'
import vocusNBN from './modules/vocus-nbn/index'
import VueModular from './plugins/module'
import router from './router'
import './app.css'
import store from './store'
import { VueApollo } from './vue-apollo'
import { initSentry, sentryUtils } from './lib/sentry'

import { fab } from '@fortawesome/free-brands-svg-icons'
import { fal } from '@fortawesome/pro-light-svg-icons'
import { fad } from '@fortawesome/pro-duotone-svg-icons'
import { fas } from '@fortawesome/pro-solid-svg-icons'
import { far } from '@fortawesome/pro-regular-svg-icons'

library.add(fab, fal, fad, fas, far)

const app = createApp({
    setup() {
        provide(ApolloClients, {
            default: defaultApolloClient,
        })
    },
    render: () => h(App),
})

app.use(router)

const isDevelopment = import.meta.env.DEV
const sentryDsn = import.meta.env.VITE_SENTRY_DSN
const sentryEnvironment =
    import.meta.env.VITE_SENTRY_ENVIRONMENT || (isDevelopment ? 'development' : 'production')

if (sentryDsn) {
    initSentry({
        app,
        router,
        dsn: sentryDsn,
        environment: sentryEnvironment,
        release: typeof __SENTRY_RELEASE__ !== 'undefined' ? __SENTRY_RELEASE__ : undefined,
        isDevelopment,
    })

    sentryUtils.setTag('app.component', 'main')
    sentryUtils.setTag('app.framework', 'vue3')
} else {
    console.warn('⚠️ Sentry DSN not configured. Error tracking disabled.')
}

app.component('FontAwesomeIcon', FontAwesomeIcon)

app.use(createPinia())
app.use(VueApollo)

app.use(VueQueryPlugin, {
    enableDevtoolsV6Plugin: false,
    queryClientConfig: {
        defaultOptions: {
            queries: {
                gcTime: 1000 * 60 * 60 * 24 * 365,
                staleTime: 1000 * 60 * 5,
                retry: false,
            },
        },
    },
})

app.use(VueModular, {
    modules: {
        pbxware,
        lmail,
        cpanel,
        vocusNBN,
        smsGlobal,
        module,
    },
    router,
    store,
})

app.config.globalProperties.$url = url
app.config.globalProperties.$sentry = sentryUtils
app.config.globalProperties.$slugify = (text, separator = '_') =>
    text
        .toString()
        .toLowerCase()
        .replace(/\s+/g, separator) // Replace spaces with _
        .replace(/&/g, `${separator}and${separator}`) // Replace & with 'and'
        .replace(/[^\w_.-]+/g, '') // Remove all non-word chars
        .replace(/__+/g, '_') // Replace multiple _ with single _

// Simple fix for reka-ui dropdown layout shift
const observer = new MutationObserver(() => {
    // Reset any scrollbar-width CSS custom property that causes layout shift
    if (document.documentElement.style.getPropertyValue('--scrollbar-width')) {
        document.documentElement.style.removeProperty('--scrollbar-width')
    }

    // Reset body styles that cause layout shift
    const body = document.body
    if (body.style.paddingRight || body.style.marginRight || body.style.overflow === 'hidden') {
        body.style.removeProperty('padding-right')
        body.style.removeProperty('margin-right')
        body.style.removeProperty('overflow')
        body.style.removeProperty('pointer-events')
    }
})

// Watch both html and body elements
observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['style'],
})

observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['style'],
})

app.mount('#app')

setupInterceptors()
